import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { getFirestore, collection, doc, onSnapshot, addDoc, updateDoc, deleteDoc, setLogLevel, setDoc, getDoc } from 'firebase/firestore';
import { ChevronLeft, ChevronRight, Plus, X, MapPin, Utensils, Zap, Trash2, Edit3, Users, LogOut, BedDouble, Plane, Bus, Ship, CableCar, TrainFront, Copy, Share2, Search, BellRing } from 'lucide-react';

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyBu49sIGGso2HGTYmmjfFn_bfGVDkc7c5s",
  authDomain: "dinsey-planner.firebaseapp.com",
  projectId: "dinsey-planner",
  storageBucket: "dinsey-planner.appspot.com",
  messagingSenderId: "117215580663",
  appId: "1:117215580663:web:24c5272b05e53cfde29ef2",
  measurementId: "G-Q9KG4B9T48"
};
const appId = 'disney-vacation-planner';

// --- Disney Data ---
const disneyResorts = {
  "Deluxe Villas": ["Bay Lake Tower at Contemporary Resort", "Boulder Ridge Villas at Disney's Wilderness Lodge", "Copper Creek Villas & Cabins at Disney's Wilderness Lodge", "Disney's Animal Kingdom Villas - Jambo House", "Disney's Animal Kingdom Villas - Kidani Village", "Disney's Beach Club Villas", "Disney's BoardWalk Villas", "Disney's Old Key West Resort", "Disney's Polynesian Villas & Bungalows", "Disney's Riviera Resort", "Disney's Saratoga Springs Resort & Spa", "The Villas at Disney's Grand Floridian Resort & Spa"],
  "Deluxe Resorts": ["Disney's Animal Kingdom Lodge", "Disney's Beach Club Resort", "Disney's BoardWalk Inn", "Disney's Contemporary Resort", "Disney's Grand Floridian Resort & Spa", "Disney's Polynesian Village Resort", "Disney's Wilderness Lodge", "Disney's Yacht Club Resort"],
  "Moderate Resorts": ["Disney's Caribbean Beach Resort", "Disney's Coronado Springs Resort", "Disney's Port Orleans Resort - French Quarter", "Disney's Port Orleans Resort - Riverside", "The Cabins at Disney's Fort Wilderness Resort"],
  "Value Resorts": ["Disney's All-Star Movies Resort", "Disney's All-Star Music Resort", "Disney's All-Star Sports Resort", "Disney's Art of Animation Resort", "Disney's Pop Century Resort"],
  "Campgrounds": ["The Campsites at Disney's Fort Wilderness Resort"]
};
const disneyParks = ["Magic Kingdom", "Epcot", "Hollywood Studios", "Animal Kingdom", "Typhoon Lagoon Water Park", "Blizzard Beach Water Park", "Disney Springs"];
const allResorts = Object.values(disneyResorts).flat();
const disneyRestaurants = {
    "Magic Kingdom": ["Be Our Guest Restaurant", "Cinderella's Royal Table", "The Crystal Palace", "The Diamond Horseshoe", "Jungle Navigation Co. LTD Skipper Canteen", "Liberty Tree Tavern", "The Plaza Restaurant", "Tony's Town Square Restaurant", "Casey's Corner", "Columbia Harbour House", "Cosmic Ray's Starlight Café", "Friar's Nook", "Golden Oak Outpost", "Pecos Bill Tall Tale Inn and Cafe", "Pinocchio Village Haus", "Sleepy Hollow", "Tomorrowland Terrace Restaurant"],
    "Epcot": ["Akershus Royal Banquet Hall", "Biergarten Restaurant", "Chefs de France", "Coral Reef Restaurant", "Garden Grill Restaurant", "Le Cellier Steakhouse", "Nine Dragons Restaurant", "Rose & Crown Dining Room", "San Angel Inn Restaurante", "Space 220 Restaurant", "Spice Road Table", "Takumi-Tei", "Teppan Edo", "Tutto Italia Ristorante", "Via Napoli Ristorante e Pizzeria", "Connections Eatery", "Crêpes À Emporter by La Crêperie de Paris", "Kringla Bakeri Og Kafe", "La Cantina de San Angel", "Les Halles Boulangerie-Patisserie", "Regal Eagle Smokehouse: Craft Drafts & Barbecue", "Sommerfest", "Sunshine Seasons", "Yorkshire County Fish Shop"],
    "Hollywood Studios": ["50's Prime Time Café", "The Hollywood Brown Derby", "Hollywood & Vine", "Mama Melrose's Ristorante Italiano", "Roundup Rodeo BBQ", "Sci-Fi Dine-In Theater Restaurant", "ABC Commissary", "Backlot Express", "Docking Bay 7 Food and Cargo", "PizzeRizzo", "Woody's Lunch Box"],
    "Animal Kingdom": ["Rainforest Cafe® at Disney's Animal Kingdom", "Tiffins Restaurant", "Yak & Yeti™ Restaurant", "Flame Tree Barbecue", "Pizzafari", "Restaurantosaurus", "Satu'li Canteen"],
    "Disney Springs": ["The BOATHOUSE®", "Chef Art Smith's Homecomin'", "City Works Eatery & Pour House", "The Edison", "Enzo's Hideaway", "House of Blues Restaurant & Bar", "Jaleo by José Andrés", "Maria & Enzo's Ristorante", "Morimoto Asia", "Paddlefish", "Paradiso 37, Taste of the Americas", "Planet Hollywood", "Raglan Road™ Irish Pub and Restaurant", "Rainforest Cafe® at Disney Springs Marketplace", "Splitsville Dining Room", "STK Orlando", "T-REX™", "Terralina Crafted Italian", "Wine Bar George", "Wolfgang Puck Bar & Grill", "Chicken Guy!", "D-Luxe Burger", "The Polite Pig", "Pizza Ponte"],
    "Resorts": ["'Ohana", "1900 Park Fare", "Ale & Compass Restaurant", "Amare", "Artist Point", "Boma - Flavors of Africa", "California Grill", "Cape May Cafe", "Cítricos", "Flying Fish", "Grand Floridian Cafe", "Hoop-Dee-Doo Musical Revue", "Jiko - The Cooking Place", "Kona Cafe", "Narcoossee's", "Olivia's Cafe", "Sanaa", "Sebastian's Bistro", "Shula's Steak House", "Steakhouse 71", "Toledo - Tapas, Steak & Seafood", "Topolino's Terrace", "Trattoria al Forno", "Whispering Canyon Cafe", "Yachtsman Steakhouse"]
};
const allRestaurants = Object.values(disneyRestaurants).flat();
const lightningLanes = {
    "Magic Kingdom": ["The Barnstormer", "Big Thunder Mountain Railroad", "Buzz Lightyear's Space Ranger Spin", "Dumbo the Flying Elephant", "Haunted Mansion", "it's a small world", "Jungle Cruise", "Mad Tea Party", "The Magic Carpets of Aladdin", "The Many Adventures of Winnie the Pooh", "Meet Cinderella and a Visiting Princess at Princess Fairytale Hall", "Meet Mickey at Town Square Theater", "Meet Tiana and a Visiting Princess at Princess Fairytale Hall", "Monsters Inc. Laugh Floor", "Peter Pan's Flight", "Pirates of the Caribbean", "Seven Dwarfs Mine Train", "Space Mountain", "TRON Lightcycle / Run", "Under the Sea ~ Journey of The Little Mermaid"],
    "Epcot": ["Disney and Pixar Short Film Festival", "Frozen Ever After", "Guardians of the Galaxy: Cosmic Rewind", "Journey into Imagination with Figment", "Living with the Land", "Mission: SPACE", "Remy's Ratatouille Adventure", "Soarin' Around the World", "Spaceship Earth", "Test Track", "The Seas with Nemo & Friends", "Turtle Talk with Crush"],
    "Hollywood Studios": ["Alien Swirling Saucers", "Beauty and the Beast Live on Stage", "Disney Junior Play & Dance!", "For the First Time in Forever: A Frozen Sing-Along Celebration", "Indiana Jones Epic Stunt Spectacular!", "Mickey & Minnie's Runaway Railway", "Millennium Falcon: Smugglers Run", "Muppet*Vision 3D", "Rock 'n' Roller Coaster Starring Aerosmith", "Slinky Dog Dash", "Star Tours – The Adventures Continue", "The Twilight Zone Tower of Terror™", "Toy Story Mania!"],
    "Animal Kingdom": ["Avatar Flight of Passage", "DINOSAUR", "Expedition Everest - Legend of the Forbidden Mountain", "Festival of the Lion King", "Finding Nemo: The Big Blue... and Beyond!", "It's Tough to be a Bug!", "Kali River Rapids", "Kilimanjaro Safaris", "Meet Favorite Disney Pals at Adventurers Outpost", "Na'vi River Journey"]
};
const allLightningLanes = Object.values(lightningLanes).flat();

// --- Main App Component ---
export default function App() {
    // --- State Management ---
    const [db, setDb] = useState(null);
    const [auth, setAuth] = useState(null);
    const [user, setUser] = useState(null);
    const [userId, setUserId] = useState(null);
    const [viewedUserId, setViewedUserId] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [sharedIdInput, setSharedIdInput] = useState('');

    const [currentDate, setCurrentDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [editingEvent, setEditingEvent] = useState(null);
    
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    
    const [detailedDay, setDetailedDay] = useState(null);
    const [view, setView] = useState('month'); // 'month', 'week', 'day'
    
    const isReadOnly = !userId || userId !== viewedUserId;

    // --- Load CSS & Fonts ---
    useEffect(() => {
        const tailwindScriptId = 'tailwind-cdn';
        if (!document.getElementById(tailwindScriptId)) {
            const script = document.createElement('script');
            script.id = tailwindScriptId;
            script.src = 'https://cdn.tailwindcss.com';
            script.async = true;
            document.head.appendChild(script);
        }

        const fontLinkId = 'waltograph-font';
        if (!document.getElementById(fontLinkId)) {
            const link = document.createElement('link');
            link.id = fontLinkId;
            link.rel = 'stylesheet';
            link.href = 'https://fonts.cdnfonts.com/css/waltograph';
            document.head.appendChild(link);
        }
    }, []);

    // --- Handle Shared URL on Load ---
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const sharedUserId = urlParams.get('userId');
        if (sharedUserId) {
            setViewedUserId(sharedUserId);
            setSharedIdInput(sharedUserId);
        }
    }, []);

    // --- Firebase Initialization & Auth State ---
    useEffect(() => {
        try {
            const app = initializeApp(firebaseConfig);
            const firestoreDb = getFirestore(app);
            const firebaseAuth = getAuth(app);
            
            setDb(firestoreDb);
            setAuth(firebaseAuth);
            setLogLevel('debug');

            const unsubscribe = onAuthStateChanged(firebaseAuth, (currentUser) => {
                if (currentUser) {
                    setUser(currentUser);
                    setUserId(currentUser.uid);
                    setViewedUserId(prev => prev || currentUser.uid);
                } else {
                    setUser(null);
                    setUserId(null);
                    if (!new URLSearchParams(window.location.search).has('userId')) {
                        setViewedUserId(null);
                    }
                }
                setIsAuthReady(true);
            });
            return () => unsubscribe();
        } catch (error) {
            console.error("Firebase initialization failed:", error);
        }
    }, []);

    // --- Firestore Data Fetching ---
    useEffect(() => {
        if (isAuthReady && db && viewedUserId) {
            const eventsCollectionPath = `artifacts/${appId}/users/${viewedUserId}/events`;
            const eventsCollection = collection(db, eventsCollectionPath);
            const unsubscribe = onSnapshot(eventsCollection, (snapshot) => {
                const fetchedEvents = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setEvents(fetchedEvents);
            }, (error) => {
                console.error("Error fetching events:", error);
                setEvents([]);
            });

            return () => unsubscribe();
        } else {
            setEvents([]);
        }
    }, [isAuthReady, db, viewedUserId]);
    
    // --- Close dropdown on outside click ---
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownRef]);
    
    // --- Auth Actions ---
    const handleGoogleSignIn = async () => { if (!auth) return; const p = new GoogleAuthProvider(); try { await signInWithPopup(auth, p); } catch (e) { console.error("Error during Google sign-in:", e); } };
    const handleGuestSignIn = async () => { if (!auth) return; try { await signInAnonymously(auth); } catch (e) { console.error("Error during guest sign-in:", e); } };
    const handleSignOut = async () => { if (!auth) return; try { await signOut(auth); setIsDropdownOpen(false); } catch (e) { console.error("Error signing out:", e); } };

    // --- Event Handlers ---
    const handleAddEvent = async (event) => { if (isReadOnly || !db || !userId) return; const p = `artifacts/${appId}/users/${userId}/events`; try { await addDoc(collection(db, p), event); } catch (e) { console.error("Error adding event:", e); } };
    const handleUpdateEvent = async (event) => { if (isReadOnly || !db || !userId) return; const r = doc(db, `artifacts/${appId}/users/${userId}/events`, event.id); try { const { id, ...d } = event; await updateDoc(r, d); } catch (e) { console.error("Error updating event:", e); } };
    const handleDeleteEvent = async (eventId) => { if (isReadOnly || !db || !userId) return; const r = doc(db, `artifacts/${appId}/users/${userId}/events`, eventId); try { await deleteDoc(r); closeModal(); } catch (e) { console.error("Error deleting event:", e); } };

    // --- Modal Control ---
    const openModal = (date) => { if (!isReadOnly) { setSelectedDate(date); setEditingEvent(null); setIsModalOpen(true); }};
    const openEditModal = (event) => { setSelectedDate(new Date(event.date + 'T00:00:00')); setEditingEvent(event); setIsModalOpen(true); };
    const closeModal = () => { setIsModalOpen(false); setEditingEvent(null); setSelectedDate(null); };
    const openDayDetail = (date) => { setDetailedDay(date); };
    const closeDayDetail = () => { setDetailedDay(null); };

    // --- Calendar Navigation ---
    const handleNext = () => {
        const newDate = new Date(currentDate);
        if (view === 'month') newDate.setMonth(newDate.getMonth() + 1);
        else if (view === 'week') newDate.setDate(newDate.getDate() + 7);
        else if (view === 'day') newDate.setDate(newDate.getDate() + 1);
        setCurrentDate(newDate);
    };

    const handlePrev = () => {
        const newDate = new Date(currentDate);
        if (view === 'month') newDate.setMonth(newDate.getMonth() - 1);
        else if (view === 'week') newDate.setDate(newDate.getDate() - 7);
        else if (view === 'day') newDate.setDate(newDate.getDate() - 1);
        setCurrentDate(newDate);
    };

    const handleToday = () => setCurrentDate(new Date());


    // --- Plan Sharing Handlers ---
    const handleLoadSharedPlan = () => { 
        if (sharedIdInput.trim()) { 
            setViewedUserId(sharedIdInput.trim()); 
            setIsDropdownOpen(false);
        }
    };
    const handleReturnToMyPlan = () => { 
        if(userId) { 
            setViewedUserId(userId); 
            setSharedIdInput(''); 
            setIsDropdownOpen(false);
        }
    };
    
    const renderView = () => {
        const props = {
            currentDate,
            events,
            onAddEventClick: openModal,
            onEventClick: openEditModal,
            onDayNumberClick: openDayDetail,
            isReadOnly,
        };
        switch (view) {
            case 'day':
                return <DayView {...props} />;
            case 'week':
                return <WeekView {...props} />;
            case 'month':
            default:
                return <MonthView {...props} />;
        }
    };
    
    const getHeaderTitle = () => {
        if (view === 'day') {
            return currentDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        }
        if (view === 'week') {
            const startOfWeek = new Date(currentDate);
            startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(endOfWeek.getDate() + 6);
            return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
        return currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    return (
        <div 
            className="min-h-screen font-sans text-gray-800 bg-cover bg-center bg-fixed"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1597466599360-3b9775841aec?q=80&w=1935&auto=format&fit=crop')" }}
        >
            <div className="p-4 sm:p-6 lg:p-8 flex flex-col min-h-screen">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 flex-grow flex flex-col">
                    <header className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
                         <div className="text-center sm:text-left">
                            <h1 className="text-4xl md:text-5xl font-bold text-blue-600" style={{ fontFamily: "'Waltograph', cursive" }}>Disney With The Rigorosos</h1>
                            <p className="text-gray-500 mt-2">Your Magical Vacation Made Easy</p>
                         </div>
                         <div className="flex-shrink-0">
                            {user ? (
                                <div className="relative" ref={dropdownRef}>
                                    <button onClick={() => setIsDropdownOpen(prev => !prev)} className="flex items-center gap-3">
                                        <span className="text-sm font-medium hidden sm:inline">Welcome, {user.isAnonymous ? 'Guest' : user.displayName || 'User'}!</span>
                                        <img 
                                            src={user.photoURL || `https://placehold.co/40x40/EBF4FF/76A9FA?text=${user.isAnonymous ? 'G' : (user.displayName ? user.displayName.charAt(0) : 'U')}`} 
                                            onError={(e) => { e.target.onerror = null; e.target.src=`https://placehold.co/40x40/EBF4FF/76A9FA?text=E`; }}
                                            alt="User profile" 
                                            className="h-10 w-10 rounded-full border-2 border-blue-200"
                                        />
                                    </button>
                                    {isDropdownOpen && <SettingsDropdown 
                                        userId={userId} 
                                        onSignOut={handleSignOut}
                                        sharedIdInput={sharedIdInput}
                                        setSharedIdInput={setSharedIdInput}
                                        handleLoadSharedPlan={handleLoadSharedPlan}
                                        isReadOnly={isReadOnly}
                                        handleReturnToMyPlan={handleReturnToMyPlan}
                                    />}
                                </div>
                            ) : (
                                isAuthReady && (
                                    <div className="flex flex-wrap items-center justify-center gap-2">
                                        <button onClick={handleGoogleSignIn} className="bg-white border border-gray-300 text-gray-700 font-semibold px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
                                            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google logo" className="h-5 w-5"/> Sign in with Google
                                        </button>
                                        <button onClick={handleGuestSignIn} className="bg-gray-500 text-white font-semibold px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors">
                                            Continue as a Guest
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                    </header>
                    
                    {userId && !isReadOnly && (
                        <CountdownWidget userId={userId} db={db} events={events} user={user} />
                    )}
                    
                    {isAuthReady && viewedUserId ? (
                         <div className="flex-grow flex flex-col bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-4 md:p-6 mt-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <button onClick={handlePrev} className="p-2 rounded-full hover:bg-gray-200 transition-colors"><ChevronLeft className="h-6 w-6" /></button>
                                    <button onClick={handleNext} className="p-2 rounded-full hover:bg-gray-200 transition-colors"><ChevronRight className="h-6 w-6" /></button>
                                    <button onClick={handleToday} className="text-sm font-semibold text-blue-500 hover:text-blue-700 px-3 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200">Today</button>
                                </div>
                                <h2 className="text-2xl font-bold text-center">{getHeaderTitle()}</h2>
                                <div className="flex items-center gap-1 bg-gray-200 rounded-lg p-1">
                                    <button onClick={() => setView('day')} className={`px-3 py-1 text-sm font-semibold rounded-md ${view === 'day' ? 'bg-white shadow' : 'text-gray-600 hover:bg-gray-100'}`}>Day</button>
                                    <button onClick={() => setView('week')} className={`px-3 py-1 text-sm font-semibold rounded-md ${view === 'week' ? 'bg-white shadow' : 'text-gray-600 hover:bg-gray-100'}`}>Week</button>
                                    <button onClick={() => setView('month')} className={`px-3 py-1 text-sm font-semibold rounded-md ${view === 'month' ? 'bg-white shadow' : 'text-gray-600 hover:bg-gray-100'}`}>Month</button>
                                </div>
                            </div>
                            {renderView()}
                         </div>
                    ) : (
                        <div 
                            className="flex-grow flex items-center justify-center bg-transparent p-6 text-center"
                        >
                            <div className="bg-white/70 backdrop-blur-sm p-8 rounded-xl">
                                <h2 className="text-2xl font-bold text-gray-800">Welcome to the Planner!</h2>
                                <p className="text-gray-600 mt-2">Please sign in or continue as a guest to start planning your trip.</p>
                            </div>
                        </div>
                    )}

                    {isModalOpen && (
                        <EventModal isOpen={isModalOpen} onClose={closeModal} onSave={editingEvent ? handleUpdateEvent : handleAddEvent} onDelete={handleDeleteEvent} selectedDate={selectedDate} event={editingEvent} isReadOnly={isReadOnly} />
                    )}
                    
                    {detailedDay && (
                        <DayDetailView 
                            date={detailedDay}
                            events={events.filter(e => e.date.startsWith(detailedDay.toISOString().split('T')[0]))}
                            onClose={closeDayDetail}
                            onAddEvent={() => {
                                closeDayDetail();
                                openModal(detailedDay);
                            }}
                            onEventClick={(event) => {
                                closeDayDetail();
                                openEditModal(event);
                            }}
                            isReadOnly={isReadOnly}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

// --- Countdown Widget Component ---
function CountdownWidget({ userId, db, events, user }) {
    const [arrivalDate, setArrivalDate] = useState(null);
    const [arrivalDateInput, setArrivalDateInput] = useState('');
    const [arrivalCountdown, setArrivalCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [diningCountdown, setDiningCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [diningDate, setDiningDate] = useState(null);
    const [showNotificationForm, setShowNotificationForm] = useState(false);
    const [notificationType, setNotificationType] = useState('email');
    const [contactInfo, setContactInfo] = useState('');
    const [notificationPref, setNotificationPref] = useState(null);

    useEffect(() => {
        if (!userId || !db) return;
        const settingsDocRef = doc(db, `artifacts/${appId}/users/${userId}/settings`, 'arrival');
        const unsubscribe = onSnapshot(settingsDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const savedDate = new Date(data.date);
                setArrivalDate(savedDate);
                const newDiningDate = new Date(savedDate);
                newDiningDate.setDate(newDiningDate.getDate() - 60);
                newDiningDate.setHours(5, 0, 0, 0); // 5 AM
                setDiningDate(newDiningDate);
                if(data.notification) {
                    setNotificationPref(data.notification);
                    setShowNotificationForm(false);
                } else {
                    setNotificationPref(null);
                }
            } else {
                setArrivalDate(null);
            }
        });
        return () => unsubscribe();
    }, [userId, db]);

    useEffect(() => {
        const calculateCountdown = (targetDate, setCountdown) => {
            const now = new Date();
            const distance = targetDate.getTime() - now.getTime();

            if (distance < 0) {
                setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }

            setCountdown({
                days: Math.floor(distance / (1000 * 60 * 60 * 24)),
                hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                seconds: Math.floor((distance % (1000 * 60)) / 1000)
            });
        };

        const timer = setInterval(() => {
            if (diningDate) calculateCountdown(diningDate, setDiningCountdown);
            if (arrivalDate) calculateCountdown(arrivalDate, setArrivalCountdown);
        }, 1000);

        return () => clearInterval(timer);
    }, [diningDate, arrivalDate]);

    const handleSetArrivalDate = async (e) => {
        e.preventDefault();
        if (!arrivalDateInput || !userId || !db) return;

        const newArrivalDate = new Date(arrivalDateInput + 'T00:00:00');
        const settingsDocRef = doc(db, `artifacts/${appId}/users/${userId}/settings`, 'arrival');
        await setDoc(settingsDocRef, { date: newArrivalDate.toISOString() });

        const newDiningDate = new Date(newArrivalDate);
        newDiningDate.setDate(newDiningDate.getDate() - 60);
        const diningDateString = newDiningDate.toISOString().split('T')[0];

        const specialEvent = events.find(ev => ev.isSpecialDiningEvent === true);
        const newEventData = {
            title: "Book Dining Reservations",
            date: diningDateString,
            time: "05:00",
            type: "dining",
            details: "Window opens to book dining reservations 60 days in advance of your arrival.",
            isSpecialDiningEvent: true
        };

        if (specialEvent) {
            const eventDocRef = doc(db, `artifacts/${appId}/users/${userId}/events`, specialEvent.id);
            await updateDoc(eventDocRef, newEventData);
        } else {
            await addDoc(collection(db, `artifacts/${appId}/users/${userId}/events`), newEventData);
        }
    };

    const handleClearDate = async () => {
        if (!userId || !db) return;
        const settingsDocRef = doc(db, `artifacts/${appId}/users/${userId}/settings`, 'arrival');
        await deleteDoc(settingsDocRef);
        const specialEvent = events.find(ev => ev.isSpecialDiningEvent === true);
        if (specialEvent) {
            const eventDocRef = doc(db, `artifacts/${appId}/users/${userId}/events`, specialEvent.id);
            await deleteDoc(eventDocRef);
        }
        setArrivalDate(null);
        setArrivalDateInput('');
    }

    const handleNotificationSubmit = async (e) => {
        e.preventDefault();
        if (!contactInfo || !diningDate) return;
        const notificationTime = new Date(diningDate.getTime() - (36 * 60 * 60 * 1000));
        const userName = user.isAnonymous ? 'Guest' : user.displayName;
        const cuteMessage = `Get Ready, ${userName}! Your Disney dining reservation window opens in 36 hours! It's time to pick your feasts. May your choices be magical! ✨`;
        
        const notificationData = {
            type: notificationType,
            contact: contactInfo,
            sendAt: notificationTime.toISOString(),
            message: cuteMessage
        };

        const settingsDocRef = doc(db, `artifacts/${appId}/users/${userId}/settings`, 'arrival');
        await setDoc(settingsDocRef, { notification: notificationData }, { merge: true });
        
        setShowNotificationForm(false);
    };

    if (!arrivalDate) {
        return (
            <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-md p-4 mb-6 max-w-2xl mx-auto w-full text-center">
                <form onSubmit={handleSetArrivalDate} className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <label htmlFor="arrival-date" className="font-semibold">Set Your Arrival Date:</label>
                    <input 
                        id="arrival-date" 
                        type="date" 
                        value={arrivalDateInput}
                        onChange={(e) => setArrivalDateInput(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg"
                    />
                    <button type="submit" className="bg-blue-500 text-white font-semibold px-4 py-2 rounded-lg hover:bg-blue-600">Set Date</button>
                </form>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 max-w-4xl mx-auto w-full">
            <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-md p-6 text-center flex flex-col justify-between">
                <h3 className="text-xl font-bold text-blue-600">Arrival Day</h3>
                <p className="mb-4 text-gray-500">{arrivalDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                <div className="grid grid-cols-4 gap-4">
                    <div><p className="text-4xl font-bold">{arrivalCountdown.days}</p><p className="text-sm text-gray-500">Days</p></div>
                    <div><p className="text-4xl font-bold">{arrivalCountdown.hours}</p><p className="text-sm text-gray-500">Hours</p></div>
                    <div><p className="text-4xl font-bold">{arrivalCountdown.minutes}</p><p className="text-sm text-gray-500">Minutes</p></div>
                    <div><p className="text-4xl font-bold">{arrivalCountdown.seconds}</p><p className="text-sm text-gray-500">Seconds</p></div>
                </div>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-md p-6 text-center flex flex-col justify-between">
                <div>
                    <h3 className="text-xl font-bold text-blue-600">Dining Reservations</h3>
                    <p className="text-gray-500 mb-4">Your booking window opens on {diningDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    <div className="grid grid-cols-4 gap-4 text-center">
                        <div><p className="text-4xl font-bold">{diningCountdown.days}</p><p className="text-sm text-gray-500">Days</p></div>
                        <div><p className="text-4xl font-bold">{diningCountdown.hours}</p><p className="text-sm text-gray-500">Hours</p></div>
                        <div><p className="text-4xl font-bold">{diningCountdown.minutes}</p><p className="text-sm text-gray-500">Minutes</p></div>
                        <div><p className="text-4xl font-bold">{diningCountdown.seconds}</p><p className="text-sm text-gray-500">Seconds</p></div>
                    </div>
                </div>
                <div className="mt-6 border-t pt-4">
                    {!notificationPref && !showNotificationForm && (
                        <button onClick={() => setShowNotificationForm(true)} className="bg-green-500 text-white font-semibold px-4 py-2 rounded-lg hover:bg-green-600 flex items-center gap-2 mx-auto">
                            <BellRing size={16} /> Get Notified
                        </button>
                    )}
                    {showNotificationForm && (
                        <form onSubmit={handleNotificationSubmit}>
                            <p className="text-sm text-gray-600 mb-2">Get a reminder 36 hours before your booking window opens.</p>
                            <div className="flex justify-center gap-4 mb-2">
                                <label><input type="radio" name="notificationType" value="email" checked={notificationType === 'email'} onChange={() => setNotificationType('email')} /> Email</label>
                                <label><input type="radio" name="notificationType" value="text" checked={notificationType === 'text'} onChange={() => setNotificationType('text')} /> Text</label>
                            </div>
                            <div className="flex justify-center gap-2">
                                <input
                                    type={notificationType === 'email' ? 'email' : 'tel'}
                                    value={contactInfo}
                                    onChange={(e) => setContactInfo(e.target.value)}
                                    placeholder={notificationType === 'email' ? 'your@email.com' : 'Phone Number'}
                                    required
                                    className="p-2 border border-gray-300 rounded-lg"
                                />
                                <button type="submit" className="bg-green-500 text-white font-semibold px-4 py-2 rounded-lg hover:bg-green-600">Sign Up</button>
                            </div>
                        </form>
                    )}
                    {notificationPref && (
                        <div className="text-sm text-green-700 bg-green-100 p-3 rounded-lg">
                            <p>Success! A reminder will be sent to <strong>{notificationPref.contact}</strong>.</p>
                            <p className="text-xs mt-1">(Note: This is a demo. No actual notification will be sent.)</p>
                        </div>
                    )}
                </div>
                <button onClick={handleClearDate} className="text-xs text-gray-400 hover:text-red-500 mt-4">Change Arrival Date</button>
            </div>
        </div>
    );
}

// --- Settings Dropdown Component ---
function SettingsDropdown({ userId, onSignOut, sharedIdInput, setSharedIdInput, handleLoadSharedPlan, isReadOnly, handleReturnToMyPlan }) {
    const [copySuccess, setCopySuccess] = useState('');

    const copyToClipboard = () => {
        const tempInput = document.createElement('input');
        const shareUrl = `${window.location.origin}${window.location.pathname}?userId=${userId}`;
        tempInput.value = shareUrl;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        setCopySuccess('Copied!');
        setTimeout(() => setCopySuccess(''), 2000);
    };

    const handleShare = () => {
        const shareUrl = `${window.location.origin}${window.location.pathname}?userId=${userId}`;
        if (navigator.share) {
            navigator.share({
                title: 'Disney Vacation Plan',
                text: `Check out our Disney vacation plan!`,
                url: shareUrl,
            })
            .then(() => console.log('Successful share'))
            .catch((error) => console.log('Error sharing', error));
        } else {
            alert('Web Share API is not supported in your browser. You can copy the link manually.');
        }
    };

    return (
        <div className="absolute mt-2 w-72 bg-white rounded-lg shadow-xl border z-50 right-0 sm:right-0 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0">
            <div className="p-4">
                <label className="text-sm font-medium text-gray-700">Copy Your Sharable Link</label>
                <div className="flex items-center mt-1">
                    <input type="text" value={`${window.location.origin}${window.location.pathname}?userId=${userId}`} readOnly className="w-full p-2 border border-gray-300 rounded-l-md bg-gray-50 text-sm" />
                    <button onClick={copyToClipboard} className="bg-gray-200 px-3 py-2 rounded-r-md hover:bg-gray-300">
                        {copySuccess ? <span className="text-green-600 text-xs">✓</span> : <Copy size={16} />}
                    </button>
                </div>
                {copySuccess && <p className="text-green-600 text-xs mt-1">{copySuccess}</p>}
            </div>
            <div className="border-t border-gray-200"></div>
            <button onClick={handleShare} className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                <Share2 className="h-5 w-5"/> Share Plan
            </button>
            <div className="border-t border-gray-200"></div>
            <div className="p-4">
                <label className="text-sm font-medium text-gray-700">View a Friend's Plan</label>
                <div className="flex items-center mt-1">
                    <input 
                        type="text" 
                        value={sharedIdInput} 
                        onChange={(e) => setSharedIdInput(e.target.value)} 
                        placeholder="Paste Session ID..." 
                        className="w-full p-2 border border-gray-300 rounded-l-md bg-gray-50 text-sm" 
                    />
                    <button onClick={handleLoadSharedPlan} className="bg-blue-500 text-white px-3 py-2 rounded-r-md hover:bg-blue-600">
                        <Users size={16} />
                    </button>
                </div>
                {isReadOnly && (
                    <div className="mt-2 text-center">
                        <button onClick={handleReturnToMyPlan} className="text-xs font-semibold text-green-600 hover:text-green-700">Return to My Plan</button>
                    </div>
                )}
            </div>
            <div className="border-t border-gray-200"></div>
            <button onClick={onSignOut} className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2">
                <LogOut className="h-5 w-5"/> Sign Out
            </button>
        </div>
    );
}


// --- MonthView Component ---
function MonthView({ currentDate, events, onAddEventClick, onEventClick, onDayNumberClick, isReadOnly }) {
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const calendarDays = useMemo(() => {
        const days = [];
        for (let i = 0; i < firstDayOfMonth; i++) days.push({ key: `blank-${i}`, isBlank: true });
        for (let day = 1; day <= daysInMonth; day++) days.push({ key: `${year}-${month}-${day}`, day, isBlank: false });
        return days;
    }, [year, month, daysInMonth, firstDayOfMonth]);

    const getEventsForDay = useCallback((day) => {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return events
            .filter(e => e.date.startsWith(dateStr))
            .sort((a, b) => {
                if (a.type === 'hotel' && b.type !== 'hotel') return -1;
                if (a.type !== 'hotel' && b.type === 'hotel') return 1;
                return (a.time || '00:00').localeCompare(b.time || '00:00');
            });
    }, [events, year, month]);

    const isToday = (day) => {
        const today = new Date();
        return year === today.getFullYear() && month === today.getMonth() && day === today.getDate();
    };

    return (
        <>
            <div className="grid grid-cols-7 text-center font-semibold text-gray-500 text-sm border-b pb-2 mb-2">
                <div>Sun</div><div>Mon</div><div>Tue</div><div>Wed</div><div>Thu</div><div>Fri</div><div>Sat</div>
            </div>
            <div className="grid grid-cols-7 flex-grow">
                {calendarDays.map((d) => {
                    if (d.isBlank) return <div key={d.key} className="border-r border-b border-gray-100"></div>;
                    const dayEvents = getEventsForDay(d.day);
                    const date = new Date(year, month, d.day);
                    return (
                        <div 
                            key={d.key} 
                            onClick={() => onDayNumberClick(date)}
                            className={`relative border-r border-b border-gray-100 p-1.5 flex flex-col group hover:bg-blue-50 transition-colors duration-300 min-h-[120px] cursor-pointer`}
                        >
                            <div 
                                className={`flex items-center justify-center h-7 w-7 rounded-full text-sm ${isToday(d.day) ? 'bg-blue-500 text-white font-bold' : ''}`}
                            >
                                {d.day}
                            </div>
                            <div className="flex-grow overflow-y-auto space-y-1 mt-1 pr-1">
                                {dayEvents.map(event => <EventPill key={event.id} event={event} onClick={() => onEventClick(event)} />)}
                            </div>
                            {!isReadOnly && (
                                <button onClick={(e) => {e.stopPropagation(); onAddEventClick(date);}} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200">
                                    <Plus className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
}

// --- WeekView Component ---
function WeekView({ currentDate, events, onAddEventClick, onEventClick, onDayNumberClick, isReadOnly }) {
    const startOfWeek = useMemo(() => {
        const date = new Date(currentDate);
        date.setDate(date.getDate() - date.getDay());
        return date;
    }, [currentDate]);

    const weekDays = useMemo(() => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(date.getDate() + i);
            days.push(date);
        }
        return days;
    }, [startOfWeek]);

    const getEventsForDay = useCallback((date) => {
        const dateStr = date.toISOString().split('T')[0];
        return events
            .filter(e => e.date.startsWith(dateStr))
            .sort((a, b) => {
                if (a.type === 'hotel' && b.type !== 'hotel') return -1;
                if (a.type !== 'hotel' && b.type === 'hotel') return 1;
                return (a.time || '00:00').localeCompare(b.time || '00:00');
            });
    }, [events]);

    return (
        <>
            <div className="grid grid-cols-7 text-center font-semibold text-gray-500 text-sm border-b pb-2 mb-2">
                {weekDays.map(day => <div key={day.toISOString()}>{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>)}
            </div>
            <div className="grid grid-cols-7 flex-grow">
                {weekDays.map(date => {
                    const dayEvents = getEventsForDay(date);
                    return (
                        <div 
                            key={date.toISOString()} 
                            onClick={() => onDayNumberClick(date)}
                            className={`relative border-r border-b border-gray-100 p-1.5 flex flex-col group hover:bg-blue-50 transition-colors duration-300 min-h-[200px] cursor-pointer`}
                        >
                             <div className="text-center font-semibold">{date.getDate()}</div>
                             <div className="flex-grow overflow-y-auto space-y-1 mt-1 pr-1">
                                {dayEvents.map(event => <EventPill key={event.id} event={event} onClick={() => onEventClick(event)} />)}
                            </div>
                             {!isReadOnly && (
                                <button onClick={(e) => {e.stopPropagation(); onAddEventClick(date);}} className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200">
                                    <Plus className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
}

// --- DayView Component ---
function DayView({ currentDate, events, onAddEventClick, onEventClick, isReadOnly }) {
    const sortedEvents = useMemo(() => {
        const dateStr = currentDate.toISOString().split('T')[0];
        return events
            .filter(e => e.date.startsWith(dateStr))
            .sort((a, b) => {
                if (a.type === 'hotel' && b.type !== 'hotel') return -1;
                if (a.type !== 'hotel' && b.type === 'hotel') return 1;
                return (a.time || '00:00').localeCompare(b.time || '00:00');
            });
    }, [events, currentDate]);

    return (
        <div className="p-4">
            {sortedEvents.length > 0 ? (
                <div className="space-y-4">
                    {sortedEvents.map(event => (
                        <div key={event.id} onClick={() => onEventClick(event)} className="bg-gray-50 p-4 rounded-lg cursor-pointer hover:bg-gray-100 shadow">
                            <div className="flex items-center justify-between">
                                <p className="font-bold text-lg">{event.title}</p>
                                <p className="text-sm font-medium text-blue-600">{event.time}</p>
                            </div>
                            <p className="text-gray-600 mt-1">{event.details}</p>
                            {event.type === 'transportation' && event.transportationType === 'flight' && (
                                <p className="text-sm text-gray-500 mt-1">
                                    {event.airline} - Flight {event.flightNumber}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-gray-500 text-center py-12">No events planned for this day.</p>
            )}
            {!isReadOnly && (
                <div className="mt-6 text-center">
                    <button onClick={() => onAddEventClick(currentDate)} className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 mx-auto">
                        <Plus className="h-4 w-4 mr-2"/> Add Event to This Day
                    </button>
                </div>
            )}
        </div>
    );
}


// --- Event Pill Component ---
function EventPill({ event, onClick }) {
    const TRANSPORT_ICONS = {
        flight: <Plane className="h-4 w-4 mr-1.5 flex-shrink-0" />,
        bus: <Bus className="h-4 w-4 mr-1.5 flex-shrink-0" />,
        boat: <Ship className="h-4 w-4 mr-1.5 flex-shrink-0" />,
        skyliner: <CableCar className="h-4 w-4 mr-1.5 flex-shrink-0" />,
        monorail: <TrainFront className="h-4 w-4 mr-1.5 flex-shrink-0" />,
    };

    const ICONS = { 
        park: <MapPin className="h-4 w-4 mr-1.5 flex-shrink-0" />, 
        dining: <Utensils className="h-4 w-4 mr-1.5 flex-shrink-0" />, 
        lightning: <Zap className="h-4 w-4 mr-1.5 flex-shrink-0" />, 
        hotel: <BedDouble className="h-4 w-4 mr-1.5 flex-shrink-0" />, 
        transportation: TRANSPORT_ICONS[event.transportationType] || <Plane className="h-4 w-4 mr-1.5 flex-shrink-0" /> 
    };
    const COLORS = { 
        park: 'bg-green-100 text-green-800', 
        dining: 'bg-red-100 text-red-800', 
        lightning: 'bg-yellow-100 text-yellow-800', 
        hotel: 'bg-purple-100 text-purple-800', 
        transportation: 'bg-cyan-100 text-cyan-800' 
    };
    
    return (
        <button onClick={(e) => {e.stopPropagation(); onClick();}} className={`w-full text-left p-1.5 rounded-lg flex items-center text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${COLORS[event.type] || 'bg-gray-100 text-gray-800'}`}>
            {ICONS[event.type]}
            <span className="truncate flex-grow">{event.title}</span>
            {event.time && <span className="ml-1.5 text-gray-500 flex-shrink-0">{event.time}</span>}
        </button>
    );
}

// --- Day Detail View Component ---
function DayDetailView({ date, events, onClose, onAddEvent, onEventClick, isReadOnly }) {
    const sortedEvents = useMemo(() => {
        return [...events].sort((a, b) => {
            if (a.type === 'hotel' && b.type !== 'hotel') return -1;
            if (a.type !== 'hotel' && b.type === 'hotel') return 1;
            return (a.time || '00:00').localeCompare(b.time || '00:00');
        });
    }, [events]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg transform transition-all">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-2xl font-bold text-gray-800">{date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</h3>
                        <button onClick={onClose} className="p-2 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600"><X className="h-5 w-5" /></button>
                    </div>
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                        {sortedEvents.length > 0 ? sortedEvents.map(event => (
                            <div key={event.id} onClick={() => onEventClick(event)} className="bg-gray-50 p-3 rounded-lg cursor-pointer hover:bg-gray-100">
                                <div className="flex items-center justify-between">
                                    <p className="font-semibold">{event.title}</p>
                                    <p className="text-sm font-medium text-blue-600">{event.time}</p>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">{event.details}</p>
                                {event.type === 'transportation' && event.transportationType === 'flight' && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        {event.airline} - Flight {event.flightNumber}
                                    </p>
                                )}
                            </div>
                        )) : (
                            <p className="text-gray-500 text-center py-8">No events planned for this day.</p>
                        )}
                    </div>
                </div>
                {!isReadOnly && (
                    <div className="bg-gray-50 px-6 py-4 flex justify-end items-center rounded-b-2xl">
                        <button onClick={onAddEvent} className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700">
                            <Plus className="h-4 w-4 mr-2"/> Add Event
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}


// --- Event Modal Component ---
function EventModal({ isOpen, onClose, onSave, onDelete, selectedDate, event, isReadOnly }) {
    const [type, setType] = useState('park');
    const [hotelSelection, setHotelSelection] = useState('');
    const [offSiteHotel, setOffSiteHotel] = useState('');
    const [hotelSearch, setHotelSearch] = useState('');
    const [isHotelDropdownOpen, setIsHotelDropdownOpen] = useState(false);
    const [parkSelection, setParkSelection] = useState('');
    const [otherPark, setOtherPark] = useState('');
    const [diningSelection, setDiningSelection] = useState('');
    const [otherDining, setOtherDining] = useState('');
    const [diningSearch, setDiningSearch] = useState('');
    const [isDiningDropdownOpen, setIsDiningDropdownOpen] = useState(false);
    const [lightningLaneSelection, setLightningLaneSelection] = useState('');
    const [otherLightningLane, setOtherLightningLane] = useState('');
    const [lightningLaneSearch, setLightningLaneSearch] = useState('');
    const [isLightningLaneDropdownOpen, setIsLightningLaneDropdownOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [details, setDetails] = useState('');
    const [time, setTime] = useState('');
    const [transportationType, setTransportationType] = useState('flight');
    const [airline, setAirline] = useState('');
    const [flightNumber, setFlightNumber] = useState('');

    const filteredRestaurants = useMemo(() => {
        if (!diningSearch) return disneyRestaurants;
        const lowercasedFilter = diningSearch.toLowerCase();
        const filtered = {};
        for (const category in disneyRestaurants) {
            const matching = disneyRestaurants[category].filter(name => name.toLowerCase().includes(lowercasedFilter));
            if (matching.length > 0) filtered[category] = matching;
        }
        return filtered;
    }, [diningSearch]);

    const filteredLightningLanes = useMemo(() => {
        if (!lightningLaneSearch) return lightningLanes;
        const lowercasedFilter = lightningLaneSearch.toLowerCase();
        const filtered = {};
        for (const category in lightningLanes) {
            const matching = lightningLanes[category].filter(name => name.toLowerCase().includes(lowercasedFilter));
            if (matching.length > 0) filtered[category] = matching;
        }
        return filtered;
    }, [lightningLaneSearch]);

    const filteredResorts = useMemo(() => {
        if (!hotelSearch) return disneyResorts;
        const lowercasedFilter = hotelSearch.toLowerCase();
        const filtered = {};
        for (const category in disneyResorts) {
            const matching = disneyResorts[category].filter(name => name.toLowerCase().includes(lowercasedFilter));
            if (matching.length > 0) filtered[category] = matching;
        }
        return filtered;
    }, [hotelSearch]);

    useEffect(() => {
        if (event) {
            setType(event.type);
            setTitle(event.title);
            setDetails(event.details || '');
            setTime(event.time || '');
            setTransportationType(event.transportationType || 'flight');
            setAirline(event.airline || '');
            setFlightNumber(event.flightNumber || '');

            if (event.type === 'hotel') {
                if (allResorts.includes(event.title)) {
                    setHotelSelection(event.title);
                    setOffSiteHotel('');
                } else {
                    setHotelSelection('Off-site');
                    setOffSiteHotel(event.title);
                }
            }
            if (event.type === 'park') {
                if (disneyParks.includes(event.title)) {
                    setParkSelection(event.title);
                    setOtherPark('');
                } else {
                    setParkSelection('Other');
                    setOtherPark(event.title);
                }
            }
            if (event.type === 'dining') {
                if (allRestaurants.includes(event.title)) {
                    setDiningSelection(event.title);
                    setOtherDining('');
                } else {
                    setDiningSelection('Other');
                    setOtherDining(event.title);
                }
            }
            if (event.type === 'lightning') {
                if (allLightningLanes.includes(event.title)) {
                    setLightningLaneSelection(event.title);
                    setOtherLightningLane('');
                } else {
                    setLightningLaneSelection('Other');
                    setOtherLightningLane(event.title);
                }
            }

        } else {
            // Reset form for new event
            setType('park');
            setTitle('');
            setDetails('');
            setTime('');
            setTransportationType('flight');
            setAirline('');
            setFlightNumber('');
            setHotelSelection('');
            setOffSiteHotel('');
            setParkSelection('');
            setOtherPark('');
            setDiningSelection('');
            setOtherDining('');
            setDiningSearch('');
            setLightningLaneSelection('');
            setOtherLightningLane('');
            setLightningLaneSearch('');
        }
    }, [event]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isReadOnly) return;
        
        let finalTitle = title;
        if (type === 'hotel') {
            finalTitle = hotelSelection === 'Off-site' ? offSiteHotel : hotelSelection;
        } else if (type === 'park') {
            finalTitle = parkSelection === 'Other' ? otherPark : parkSelection;
        } else if (type === 'dining') {
            finalTitle = diningSelection === 'Other' ? otherDining : diningSelection;
        } else if (type === 'lightning') {
            finalTitle = lightningLaneSelection === 'Other' ? otherLightningLane : lightningLaneSelection;
        }

        const dateString = selectedDate.toISOString().split('T')[0];
        const eventData = { 
            date: dateString, 
            type, 
            title: finalTitle, 
            details, 
            time: type === 'hotel' ? '' : time,
            ...(type === 'transportation' && { transportationType, airline, flightNumber })
        };
        if (event) eventData.id = event.id;
        onSave(eventData);
        onClose();
    };

    const handleDeleteClick = () => { if (!isReadOnly && event) { onDelete(event.id); }};
    
    const formattedDate = selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md transform transition-all overflow-hidden">
                <form onSubmit={handleSubmit}>
                    <div className="p-6">
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-xl font-bold">{isReadOnly ? 'View Event' : (event ? 'Edit Event' : 'Add Event')}</h3>
                                <p className="text-sm text-gray-500">{formattedDate}</p>
                            </div>
                            <button type="button" onClick={onClose} className="p-2 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="mt-6 space-y-4">
                            <div>
                                <label className="text-sm font-medium">Type</label>
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                    <button type="button" disabled={isReadOnly} onClick={() => setType('park')} className={`flex items-center justify-center p-2 rounded-lg border-2 ${type === 'park' ? 'border-green-500 bg-green-50' : 'border-gray-200'} ${isReadOnly ? 'cursor-not-allowed opacity-70' : ''}`}><MapPin className="h-5 w-5 text-green-600 mr-2"/> Park</button>
                                    <button type="button" disabled={isReadOnly} onClick={() => setType('dining')} className={`flex items-center justify-center p-2 rounded-lg border-2 ${type === 'dining' ? 'border-red-500 bg-red-50' : 'border-gray-200'} ${isReadOnly ? 'cursor-not-allowed opacity-70' : ''}`}><Utensils className="h-5 w-5 text-red-600 mr-2"/> Dining</button>
                                    <button type="button" disabled={isReadOnly} onClick={() => setType('lightning')} className={`flex items-center justify-center p-2 rounded-lg border-2 ${type === 'lightning' ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200'} ${isReadOnly ? 'cursor-not-allowed opacity-70' : ''}`}><Zap className="h-5 w-5 text-yellow-600 mr-2"/> Lightning Lane</button>
                                    <button type="button" disabled={isReadOnly} onClick={() => setType('hotel')} className={`flex items-center justify-center p-2 rounded-lg border-2 ${type === 'hotel' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'} ${isReadOnly ? 'cursor-not-allowed opacity-70' : ''}`}><BedDouble className="h-5 w-5 text-purple-600 mr-2"/> Hotel</button>
                                    <button type="button" disabled={isReadOnly} onClick={() => setType('transportation')} className={`flex items-center justify-center p-2 rounded-lg border-2 ${type === 'transportation' ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200'} ${isReadOnly ? 'cursor-not-allowed opacity-70' : ''}`}><Plane className="h-5 w-5 text-cyan-600 mr-2"/> Transport</button>
                                </div>
                            </div>

                            {type === 'hotel' && (
                                <div className="relative">
                                    <label htmlFor="hotel-search" className="text-sm font-medium">Resort</label>
                                    <div className="relative">
                                         <input
                                            id="hotel-search"
                                            type="text"
                                            value={hotelSelection === 'Off-site' ? offSiteHotel : hotelSearch || hotelSelection}
                                            onFocus={() => {setIsHotelDropdownOpen(true); setHotelSearch('')}}
                                            onChange={(e) => {
                                                setHotelSearch(e.target.value);
                                                setHotelSelection('');
                                                setIsHotelDropdownOpen(true);
                                            }}
                                            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Search for a resort..."
                                        />
                                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    </div>
                                   
                                    {isHotelDropdownOpen && (
                                        <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-md mt-1 max-h-60 overflow-y-auto">
                                            {Object.entries(filteredResorts).map(([category, resorts]) => (
                                                <div key={category}>
                                                    <h4 className="font-bold text-sm text-gray-500 px-3 py-2 bg-gray-50 sticky top-0">{category}</h4>
                                                    {resorts.map(name => (
                                                        <button
                                                            key={name}
                                                            type="button"
                                                            onClick={() => {
                                                                setHotelSelection(name);
                                                                setHotelSearch(name);
                                                                setIsHotelDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                                        >
                                                            {name}
                                                        </button>
                                                    ))}
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setHotelSelection('Off-site');
                                                    setIsHotelDropdownOpen(false);
                                                }}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 font-semibold text-blue-600"
                                            >
                                                Off-site...
                                            </button>
                                        </div>
                                    )}
                                     {hotelSelection === 'Off-site' && (
                                        <div className="mt-2">
                                            <label htmlFor="off-site-hotel" className="text-sm font-medium">Off-site Hotel Name</label>
                                            <input id="off-site-hotel" type="text" value={offSiteHotel} onChange={e => setOffSiteHotel(e.target.value)} required disabled={isReadOnly} className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" />
                                        </div>
                                    )}
                                </div>
                            )}

                            {type === 'lightning' && (
                                <div className="relative">
                                    <label htmlFor="lightning-search" className="text-sm font-medium">Attraction</label>
                                    <div className="relative">
                                         <input
                                            id="lightning-search"
                                            type="text"
                                            value={lightningLaneSelection === 'Other' ? otherLightningLane : lightningLaneSearch || lightningLaneSelection}
                                            onFocus={() => {setIsLightningLaneDropdownOpen(true); setLightningLaneSearch('')}}
                                            onChange={(e) => {
                                                setLightningLaneSearch(e.target.value);
                                                setLightningLaneSelection('');
                                                setIsLightningLaneDropdownOpen(true);
                                            }}
                                            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Search for a Lightning Lane..."
                                        />
                                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    </div>
                                   
                                    {isLightningLaneDropdownOpen && (
                                        <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-md mt-1 max-h-60 overflow-y-auto">
                                            {Object.entries(filteredLightningLanes).map(([category, lanes]) => (
                                                <div key={category}>
                                                    <h4 className="font-bold text-sm text-gray-500 px-3 py-2 bg-gray-50 sticky top-0">{category}</h4>
                                                    {lanes.map(name => (
                                                        <button
                                                            key={name}
                                                            type="button"
                                                            onClick={() => {
                                                                setLightningLaneSelection(name);
                                                                setLightningLaneSearch(name);
                                                                setIsLightningLaneDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                                        >
                                                            {name}
                                                        </button>
                                                    ))}
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setLightningLaneSelection('Other');
                                                    setIsLightningLaneDropdownOpen(false);
                                                }}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 font-semibold text-blue-600"
                                            >
                                                Other...
                                            </button>
                                        </div>
                                    )}
                                     {lightningLaneSelection === 'Other' && (
                                        <div className="mt-2">
                                            <label htmlFor="other-lightning-lane" className="text-sm font-medium">Other Attraction Name</label>
                                            <input id="other-lightning-lane" type="text" value={otherLightningLane} onChange={e => setOtherLightningLane(e.target.value)} required disabled={isReadOnly} className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" />
                                        </div>
                                    )}
                                </div>
                            )}

                            {type === 'dining' && (
                                <div className="relative">
                                    <label htmlFor="dining-search" className="text-sm font-medium">Restaurant</label>
                                    <div className="relative">
                                         <input
                                            id="dining-search"
                                            type="text"
                                            value={diningSelection === 'Other' ? otherDining : diningSearch || diningSelection}
                                            onFocus={() => {setIsDiningDropdownOpen(true); setDiningSearch('')}}
                                            onChange={(e) => {
                                                setDiningSearch(e.target.value);
                                                setDiningSelection('');
                                                setIsDiningDropdownOpen(true);
                                            }}
                                            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Search for a restaurant..."
                                        />
                                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    </div>
                                   
                                    {isDiningDropdownOpen && (
                                        <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-md mt-1 max-h-60 overflow-y-auto">
                                            {Object.entries(filteredRestaurants).map(([category, restaurants]) => (
                                                <div key={category}>
                                                    <h4 className="font-bold text-sm text-gray-500 px-3 py-2 bg-gray-50 sticky top-0">{category}</h4>
                                                    {restaurants.map(name => (
                                                        <button
                                                            key={name}
                                                            type="button"
                                                            onClick={() => {
                                                                setDiningSelection(name);
                                                                setDiningSearch(name);
                                                                setIsDiningDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                                        >
                                                            {name}
                                                        </button>
                                                    ))}
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setDiningSelection('Other');
                                                    setIsDiningDropdownOpen(false);
                                                }}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 font-semibold text-blue-600"
                                            >
                                                Other...
                                            </button>
                                        </div>
                                    )}
                                     {diningSelection === 'Other' && (
                                        <div className="mt-2">
                                            <label htmlFor="other-dining" className="text-sm font-medium">Other Restaurant Name</label>
                                            <input id="other-dining" type="text" value={otherDining} onChange={e => setOtherDining(e.target.value)} required disabled={isReadOnly} className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" />
                                        </div>
                                    )}
                                </div>
                            )}

                            {type === 'park' && (
                                <>
                                    <div>
                                        <label htmlFor="park-select" className="text-sm font-medium">Park</label>
                                        <select 
                                            id="park-select"
                                            value={parkSelection}
                                            onChange={(e) => setParkSelection(e.target.value)}
                                            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="" disabled>Select a Park</option>
                                            {disneyParks.map(park => <option key={park} value={park}>{park}</option>)}
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                    {parkSelection === 'Other' && (
                                        <div>
                                            <label htmlFor="other-park" className="text-sm font-medium">Other Location Name</label>
                                            <input id="other-park" type="text" value={otherPark} onChange={e => setOtherPark(e.target.value)} required={parkSelection === 'Other'} disabled={isReadOnly} className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200" />
                                        </div>
                                    )}
                                </>
                            )}

                            {type !== 'hotel' && type !== 'park' && type !== 'dining' && type !== 'lightning' &&(
                                <div>
                                    <label htmlFor="title" className="text-sm font-medium">Title</label>
                                    <input id="title" type="text" value={title} onChange={e => setTitle(e.target.value)} required disabled={isReadOnly} className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200" />
                                </div>
                            )}

                            {type === 'transportation' && (
                                <div>
                                    <label className="text-sm font-medium">Mode</label>
                                    <div className="grid grid-cols-5 gap-2 mt-2">
                                        <button type="button" onClick={() => setTransportationType('flight')} className={`flex items-center justify-center p-2 rounded-lg border-2 ${transportationType === 'flight' ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200'}`}><Plane className="h-5 w-5 text-cyan-600"/></button>
                                        <button type="button" onClick={() => setTransportationType('bus')} className={`flex items-center justify-center p-2 rounded-lg border-2 ${transportationType === 'bus' ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200'}`}><Bus className="h-5 w-5 text-cyan-600"/></button>
                                        <button type="button" onClick={() => setTransportationType('boat')} className={`flex items-center justify-center p-2 rounded-lg border-2 ${transportationType === 'boat' ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200'}`}><Ship className="h-5 w-5 text-cyan-600"/></button>
                                        <button type="button" onClick={() => setTransportationType('skyliner')} className={`flex items-center justify-center p-2 rounded-lg border-2 ${transportationType === 'skyliner' ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200'}`}><CableCar className="h-5 w-5 text-cyan-600"/></button>
                                        <button type="button" onClick={() => setTransportationType('monorail')} className={`flex items-center justify-center p-2 rounded-lg border-2 ${transportationType === 'monorail' ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200'}`}><TrainFront className="h-5 w-5 text-cyan-600"/></button>
                                    </div>
                                </div>
                            )}

                            {type === 'transportation' && transportationType === 'flight' && (
                                <>
                                    <div>
                                        <label htmlFor="airline" className="text-sm font-medium">Airline</label>
                                        <input id="airline" type="text" value={airline} onChange={e => setAirline(e.target.value)} disabled={isReadOnly} className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200" />
                                    </div>
                                    <div>
                                        <label htmlFor="flightNumber" className="text-sm font-medium">Flight Number</label>
                                        <input id="flightNumber" type="text" value={flightNumber} onChange={e => setFlightNumber(e.target.value)} disabled={isReadOnly} className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200" />
                                    </div>
                                </>
                            )}

                            {type !== 'hotel' && (
                                <div>
                                    <label htmlFor="time" className="text-sm font-medium">Time</label>
                                    <input id="time" type="time" value={time} onChange={e => setTime(e.target.value)} disabled={isReadOnly} className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200" />
                                </div>
                            )}
                             <div>
                                <label htmlFor="details" className="text-sm font-medium">Details</label>
                                <textarea id="details" value={details} onChange={e => setDetails(e.target.value)} rows="3" disabled={isReadOnly} className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200"></textarea>
                            </div>
                        </div>
                    </div>
                    {!isReadOnly && (
                        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center rounded-b-2xl">
                            {event ? (
                                <button type="button" onClick={handleDeleteClick} className="flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-red-100 rounded-lg hover:bg-red-200"><Trash2 className="h-4 w-4 mr-2" />Delete</button>
                            ) : <div></div>}
                            <button type="submit" className="flex items-center justify-center px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                {event ? <Edit3 className="h-4 w-4 mr-2"/> : <Plus className="h-4 w-4 mr-2"/>}
                                {event ? 'Save Changes' : 'Add Event'}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
